import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MailCheck, Send, Eye, MapPin, CalendarDays, Clock, AlertTriangle, CheckCircle2, Paperclip, X } from "lucide-react";

const auDate = (s) => { if (!s) return "—"; const [y, m, d] = s.split("-"); return `${d}/${m}/${y}`; };

export default function JobConfirmations() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = () => { setLoading(true); api.get("/jobs/pending-confirmation").then((r) => setJobs(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const [marking, setMarking] = useState(null);
  const markDone = async (j) => {
    setMarking(j.id);
    try {
      await api.post(`/jobs/${j.id}/mark-confirmed`);
      toast.success("Marked as confirmed — removed from the queue.");
      setJobs((prev) => prev.filter((x) => x.id !== j.id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setMarking(null); }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="job-confirmations-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center"><MailCheck className="w-6 h-6 text-teal-600" /></div>
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy">Job Confirmations</h1>
          <p className="text-slate-500 mt-0.5">Booking confirmation emails awaiting send. Nothing sends automatically — review &amp; send each one.</p>
        </div>
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm">Loading…</p>
      ) : jobs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center shadow-sm" data-testid="confirmations-empty">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <p className="text-navy font-heading font-semibold text-lg">All caught up</p>
          <p className="text-slate-500 text-sm mt-1">No job confirmations are waiting to be sent.</p>
        </div>
      ) : (
        <>
        {jobs.some((j) => j.urgent) && (
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 mb-4" data-testid="urgent-banner">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800"><span className="font-semibold">{jobs.filter((j) => j.urgent).length}</span> of these start within 3 days — send their confirmations first.</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[...jobs].sort((a, b) => (b.urgent === a.urgent ? 0 : b.urgent ? 1 : -1)).map((j) => {
            const du = j.days_until;
            const dueLabel = du == null ? null : du < 0 ? `${Math.abs(du)}d overdue` : du === 0 ? "Today" : du === 1 ? "Tomorrow" : `In ${du} days`;
            return (
            <div key={j.id} data-testid={`confirmation-card-${j.id}`} className={`bg-white rounded-2xl border p-5 shadow-sm space-y-3 ${j.urgent ? "border-amber-300 ring-1 ring-amber-200" : "border-slate-100"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-heading font-semibold text-navy truncate">{j.customer_name}</p>
                  <p className="text-xs text-slate-400">{j.job_number}</p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {j.status === "resend"
                    ? <span className="text-[10px] uppercase font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1" title="Date/time changed since last sent"><AlertTriangle className="w-3 h-3" />Re-send</span>
                    : <span className="text-[10px] uppercase font-bold px-2 py-1 rounded-full bg-blue-100 text-blue-700">New</span>}
                  {j.urgent && dueLabel && <span data-testid={`due-badge-${j.id}`} className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full ${du < 0 ? "bg-red-100 text-red-700" : "bg-orange-100 text-orange-700"}`}>{dueLabel}</span>}
                </div>
              </div>
              <div className="space-y-1.5 text-sm text-slate-600">
                <p className="flex items-start gap-2"><MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />{j.site_address || "—"}</p>
                <p className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-slate-400" />{auDate(j.appointment_date)}</p>
                <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" />{j.start_time || "TBC"} – {j.finish_time || "TBC"}</p>
                {!j.accounts_email_on_file && <p className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />No accounts email on file — the email will request one.</p>}
              </div>
              <Button className="w-full bg-navy hover:bg-navy/90" data-testid={`review-send-${j.id}`} onClick={() => setActive(j)}>
                <Eye className="w-4 h-4 mr-2" />Review &amp; Send
              </Button>
              <Button variant="outline" className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                data-testid={`mark-confirmed-${j.id}`} disabled={marking === j.id} onClick={() => markDone(j)}>
                <CheckCircle2 className="w-4 h-4 mr-2" />{marking === j.id ? "Marking…" : "Already confirmed — mark as done"}
              </Button>
            </div>
          );})}
        </div>
        </>
      )}

      {active && <SendDialog job={active} onClose={() => setActive(null)} onSent={() => { setActive(null); load(); }} />}
    </div>
  );
}

function SendDialog({ job, onClose, onSent }) {
  const [preview, setPreview] = useState(null);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [includeQuote, setIncludeQuote] = useState(false);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(`/jobs/${job.id}/confirmation-preview`).then((r) => {
      setPreview(r.data); setTo(r.data.suggested_recipient || "");
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [job.id]);

  const addFiles = (list) => setFiles((prev) => [...prev, ...Array.from(list || [])]);
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const send = async () => {
    if (!to.trim()) { toast.error("Enter a recipient email."); return; }
    setSending(true);
    const fd = new FormData();
    fd.append("to", to.trim());
    fd.append("cc", cc);
    fd.append("include_quote_pdf", includeQuote ? "true" : "false");
    files.forEach((f) => fd.append("files", f));
    try {
      const r = await api.post(`/jobs/${job.id}/send-confirmation`, fd);
      toast.success(r.data.message || "Confirmation sent.");
      onSent();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="send-confirmation-dialog">
        <DialogHeader><DialogTitle>Send Job Confirmation — {job.customer_name}</DialogTitle></DialogHeader>
        {!preview ? (
          <p className="text-slate-400 text-sm">Loading preview…</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Send to <span className="text-red-500">*</span></Label>
                <Input data-testid="confirmation-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@email.com" className="mt-1" />
              </div>
              <div>
                <Label>CC (optional)</Label>
                <Input data-testid="confirmation-cc" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="accounts@email.com" className="mt-1" />
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 p-3 space-y-2 text-sm">
              <p className="font-semibold text-navy flex items-center gap-2"><Paperclip className="w-4 h-4" />Attachments</p>
              {preview.swms?.has_file
                ? <p className="text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />SWMS: {preview.swms.filename} (v{preview.swms.version}) — attached automatically</p>
                : <p className="text-amber-600 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />No SWMS on file — upload one under Email &amp; Communications → Job Confirmations.</p>}
              {preview.has_quote && (
                <label className="flex items-center gap-2 cursor-pointer text-slate-600">
                  <input type="checkbox" checked={includeQuote} onChange={(e) => setIncludeQuote(e.target.checked)} data-testid="confirmation-include-quote" className="w-4 h-4" />
                  Attach Quote PDF
                </label>
              )}
              <div className="flex flex-wrap gap-2 items-center">
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="confirmation-add-files">Add files</Button>
                <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} />
                {files.map((f, i) => (
                  <span key={i} className="text-xs bg-slate-100 border border-slate-200 rounded-full px-2 py-1 flex items-center gap-1">
                    {f.name}<button onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Preview</p>
              <div className="text-sm mb-2"><span className="text-slate-400">Subject:</span> <span className="font-semibold text-navy" data-testid="confirmation-preview-subject">{preview.subject}</span></div>
              <div className="border border-slate-100 rounded-xl p-3 bg-slate-50 max-h-72 overflow-y-auto" dangerouslySetInnerHTML={{ __html: preview.html }} data-testid="confirmation-preview-html" />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={send} disabled={sending} data-testid="confirmation-send-btn">
                <Send className="w-4 h-4 mr-2" />{sending ? "Sending…" : "Send Confirmation"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
